import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, OnChanges, SimpleChanges, Output, ViewChild, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subject, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { LeadList } from 'src/app/models/LeadList';

import { Lead } from '../../../models/Lead';
import { ConfirmationService } from '../../../shared/components/confirmation-modal/confirmation.service';
import { ToastService } from '../../../shared/components/toast/toast.service';
import { PaginatedResult } from '../../../shared/model/paginated-result';
import { LeadsService } from '../../../shared/services/leads.service';
import { ListsService } from '../../../shared/services/lists.service';
import { AuthService } from 'src/app/shared/services/auth.service';
import { User } from 'src/app/models/User';
import { UtilService } from 'src/app/shared/services/util.service'
import { readNumberPreference, saveNumberPreference } from '../../../shared/utils/list-preferences';

/** Itens por página dos leads (pedido do Misael, 14/10/2026). Preferência fica no navegador. */
const LEADS_PER_PAGE_OPTIONS = [10, 50, 100] as const;
const LEADS_PER_PAGE_KEY = 'lists.leads.per-page';
export const LEAD_IMPORT_EXTENSIONS = '.csv,.txt,.xlsx,.xls';

@Component({
  selector: 'app-leads',
  templateUrl: './leads.component.html',
  styleUrls: ['./leads.component.scss']
})
export class LeadsComponent implements OnInit, OnChanges, OnDestroy {

  @Input() leadListId: string;
  @Input() showImportButton = true;
  @Input() reload: EventEmitter<boolean>;
  @Input() showPostbackButton: boolean;
  @Output() onImport = new Subject<LeadList>();

  page = 1;
  readonly perPageOptions: readonly number[] = LEADS_PER_PAGE_OPTIONS;
  perPage = readNumberPreference(LEADS_PER_PAGE_KEY, 10, LEADS_PER_PAGE_OPTIONS);
  /** Formatos do import de listas (CU-17tm466pyc5); vai para o `accept` e para a checagem do app-file. */
  readonly importExtensions = LEAD_IMPORT_EXTENSIONS;
  fromFile: boolean;
  deleteLeads: boolean;
  exportLeads: boolean;
  file: File;
  text: string;
  filter = '';
  importError: boolean;
  animateError: boolean;
  result: PaginatedResult<Lead[]>;
  selectedLead: any;

  subscriptions: Subscription[] = [];
  private leadsSub?: Subscription;
  leadText: string;

  exportWholeList = true;
  exportStartDate: Date;
  exportEndDate: Date;

  @ViewChild('modalLeadText') leadTextModal: NgbModalRef;

  user: User;
  isUserValidated: boolean = false;
  isLoading: boolean = false;
  private isInitialLoad: boolean = true;

  // Apresentação (M9 / achado A8 do PLANO.md): não existia sinal visual de erro
  // na carga dos leads (a falha só desligava `isLoading` e mantinha o `result`
  // anterior). `loadError` não muda a chamada nem o tratamento, só o que aparece.
  loadError = false;

  // Menu "⋯" das ações de cópia (PIX, rastreio, checkout/boleto/pix/customizada/
  // membros), no mesmo padrão do `broadcasts.component.ts`.
  openMenuId: string | null = null;


  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private toastService: ToastService,
    private modalService: NgbModal,
    private leadsService: LeadsService,
    private listsService: ListsService,
    private confirmationService: ConfirmationService,
    private authService: AuthService,
    private UtilService: UtilService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.user = this.authService.getUser();
    this.isUserValidated = this.user.validated;
    this.isLoading = false;

    this.subscriptions.push(
      this.activatedRoute.queryParams.subscribe(params => {
        const newPage = Number(params.page) || 1;
        const pageChanged = this.page !== newPage;
        this.page = newPage;
        this.UtilService.getExportLeads(this.user.id)
        
        // Só chama listPaginated após carregamento inicial e se a página mudou
        if (!this.isInitialLoad && pageChanged && this.leadListId) {
          this.listPaginated();
        }
      })
    );

    if (this.reload) {
      this.subscriptions.push(
        this.reload.subscribe(() => {
          this.isInitialLoad = false;
          this.listPaginated();
        })
      );
    }

    this.subscriptions.push(
      this.UtilService.exportLeads$.subscribe(value => {
        this.exportLeads = value;
        this.cdr.markForCheck();
      })
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Quando leadListId é definido pela primeira vez ou muda
    if (changes.leadListId && changes.leadListId.currentValue) {
      // Se já passou do carregamento inicial, carrega os dados
      // Caso contrário, aguarda o reload do componente pai
      if (!this.isInitialLoad) {
        this.listPaginated();
      }
      if (!this.reload) {
        // Fallback: se não há evento reload, carrega diretamente
        this.isInitialLoad = false;
        this.listPaginated();
      }
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
    this.leadsSub?.unsubscribe();
  }

  listPaginated(): void {
    // A flag liga DENTRO da guarda: sem lista escolhida nenhuma requisição sai,
    // e ligá-la antes deixaria o esqueleto brilhando à espera do que não vem.
    // Antes isso só desabilitava o campo de busca e passava despercebido.
    // Uma requisição por vez (regra 7, achado do revisor 14/10): sem cancelar
    // a anterior, uma resposta atrasada (página trocada rápido, delete seguido
    // de reloadLeads) podia sobrescrever `result`/`loadError` com estado velho.
    if (this.leadListId) {
      this.leadsSub?.unsubscribe();
      this.isLoading = true;
      this.loadError = false;

      if (this.result) {
        this.result.total = 0;
      }
      this.leadsSub = this.leadsService.listByLeadListId(this.leadListId, this.page, this.perPage, this.filter).subscribe(result => {
        this.result = result;
        if (result.last_page < this.page) {
          this.pageChanged(result.last_page);
        }
        this.isLoading = false;
      }, () => {
        this.isLoading = false;
        this.loadError = true;
      });
    }
  }

  toggleMenu(leadId: string, event: MouseEvent): void {
    event.stopPropagation();
    this.openMenuId = this.openMenuId === leadId ? null : leadId;
  }

  @HostListener('document:click')
  closeMenu(): void {
    this.openMenuId = null;
  }

  // OR das condições que hoje decidem cada botão de cópia da linha (ver §1 do
  // PLANO.md) — só diz se o menu "⋯" tem algo para mostrar, nenhuma delas muda.
  hasCopyActions(lead: Lead): boolean {
    return !!(
      lead.pix_code ||
      lead.tracking_code ||
      lead.tracking_url ||
      this.getCheckoutUrl(lead) ||
      this.getTicketUrl(lead) ||
      this.getPixUrl(lead) ||
      this.getCustomizedUrl(lead) ||
      this.getMembersUrl(lead)
    );
  }

  getWhatsapp(lead: Lead) {
    let phone = lead.phone;
    if (phone.length <= 11) {
      phone = `55${phone}`
    }
    let text = 'Oi';
    if (lead.name) {
      text += ', ' + lead.name.split(' ')[0];
    }
    return `https://wa.me/+${phone}?text=${text}`;
  }

  getCheckoutUrl(lead: Lead): string {
    return lead.checkout_short_url || lead.checkout_url;
  }

  getTicketUrl(lead: Lead): string {
    return lead.ticket_short_url || lead.ticket_url;
  }

  getPixUrl(lead: Lead): string {
    return lead.pix_short_url || lead.pix_url;
  }

  getMembersUrl(lead: Lead): string {
    return lead.members_short_url || lead.members_url;
  }

  getCustomizedUrl(lead: Lead): string {
    return lead.customized_short_url || lead.customized_url;
  }

  async copy(text: string, url: string) {
    try {
      await navigator.clipboard.writeText(url);
      this.toastService.success(`${text} copiada com sucesso`);
    } catch (e) {
      this.toastService.error(`Erro ao copiar ${text}`);
    }
  }

  open(content: any): void {
    this.file = null;
    this.text = '';
    this.animateError = false;
    this.importError = false;
    this.deleteLeads = false;
    this.modalService.open(content);
  }

  import(modal: any): void {
    if (!this.file && !this.text) {
      this.importError = true;
      this.animateError = true;
      setTimeout(() => {
        this.animateError = false;
      }, 2000);
      return;
    }

    if (this.deleteLeads) {
      this.confirmationService.show('Você irá excluir todos os leads desta lista. Deseja continuar?', () => this.doImport(modal));
    } else {
      this.doImport(modal);
    }
  }

  doImport(modal: any) {
    this.leadsService.import(this.leadListId, this.file, this.text, this.deleteLeads)
      .pipe(finalize(() => modal.close()))
      .subscribe(res => {
        this.listPaginated();
        this.toastService.success('Sua importação está em progresso!');
        this.onImport.next(res);
      },
        res => this.toastService.error(res.error.message || 'Erro ao importar! Tente novamente mais tarde.')
      )
  }

  openExport(content: any): void {
    this.exportWholeList = true;
    this.exportStartDate = null;
    this.exportEndDate = null;
    this.modalService.open(content);
  }

  get invalidExportPeriod(): boolean {
    return !this.exportWholeList
      && !!this.exportStartDate
      && !!this.exportEndDate
      && this.exportStartDate > this.exportEndDate;
  }

  export(modal: any): void {
    if (this.exportWholeList) {
      this.leadsService.export(this.leadListId);
    } else {
      if (!this.exportStartDate && !this.exportEndDate) {
        this.toastService.error('Informe ao menos uma data para exportar por período.');
        return;
      }
      if (this.invalidExportPeriod) {
        this.toastService.error('Período inválido. A data inicial deve ser anterior à data final.');
        return;
      }
      const start = this.exportStartDate ? this.toIsoDate(this.exportStartDate) : null;
      const end = this.exportEndDate ? this.toIsoDate(this.exportEndDate) : null;
      this.leadsService.export(this.leadListId, start, end);
    }
    modal.close();
  }

  private toIsoDate(date: Date): string {
    return new Date(date).toISOString().split('T')[0];
  }

  showPostbacks(lead: Lead): boolean {
    const createdAt = new Date(lead.created_at);
    const actualDate = new Date();

    const oneDayInMilliseconds = 24 * 60 * 60 * 1000;
    const millisecondsTotal = Math.abs(actualDate.getTime() - createdAt.getTime());
    const days = Math.floor(millisecondsTotal / oneDayInMilliseconds) + 1;

    return days < 7;
  }

  changeFile(file: File): void {
    this.file = file;
    this.importError = false;
  }

  changePerPage(perPage: number): void {
    if (!perPage || perPage === this.perPage) {
      return;
    }
    this.perPage = perPage;
    saveNumberPreference(LEADS_PER_PAGE_KEY, perPage);
    // Página 1: a página atual pode não existir no tamanho novo. Navegar dispara
    // o `queryParams` → `listPaginated()`; já na 1, recarrega direto.
    if (this.page !== 1) {
      this.pageChanged(1);
    } else {
      this.listPaginated();
    }
  }

  pageChanged(page: number): void {
    this.router.navigate(['/lists/edit', this.leadListId], { queryParams: { page } });
  }

  confirmDelete(lead: Lead): void {
    this.confirmationService.show(`Deseja realmente excluir o(a) ${lead.name}?`, () => this.delete(lead.id));
  }

  delete(id: string): void {
    this.subscriptions.push(
      this.leadsService.delete(id).subscribe(
        () => {
          this.toastService.success('Lead excluído com sucesso!');
          this.listPaginated();
        },
        res => this.toastService.error(res.error.message || 'Erro ao excluir Lead! Tente novamente mais tarde.')
      )
    );
  }

  showLeadText(lead: Lead): void {
    this.leadText = lead.message_content;
    this.open(this.leadTextModal);
  }

  haveVars(lead: Lead): boolean {
    if (lead.var1 || lead.var2 || lead.var3 || lead.var4 || lead.var5) {
      return true;
    }
    return false;
  }
}
